import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { CustomDatePicker, CustomDropdown, todayCalendarDate, formatCalendarDate, getAssigneeIdFromChartClick } from '@medprompt/shared';
import { InsightsService, sharedApiClient } from '../fetcher';
import './DocumentAssigneeModal.css';

const DocumentAssigneeModal = ({ 
  isOpen, 
  onClose, 
  activeUsers = [], 
  teams = [],
  onAssigneeClick
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [documentsAssigned, setDocumentsAssigned] = useState([]);
  const [missingCount, setMissingCount] = useState(0);
  
  // Date range state
  const [dateRange, setDateRange] = useState({ value: 'today', display: 'Today' });
  const [customDateRange, setCustomDateRange] = useState({
    from: todayCalendarDate(),
    to: todayCalendarDate()
  });
  
  // Dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  const insightsService = useMemo(() => new InsightsService(sharedApiClient), []);

  const assignmentCounts = useMemo(() => {
    const counts = {};
    documentsAssigned.forEach(doc => {
      if (doc.assigneeId) {
        counts[doc.assigneeId] = (counts[doc.assigneeId] || 0) + 1;
      }
    });
    return counts;
  }, [documentsAssigned]);

  // Calculate date range based on selection
  const getDateRange = useCallback(() => {
    const now = new Date();
    let to = new Date();
    let from;

    switch (dateRange.value) {
      case 'today':
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'yesterday':
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        to = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'last24h':
        from = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case 'last7days':
        from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'custom':
        from = new Date(customDateRange.from);
        const selectedToDate = new Date(customDateRange.to);
        
        // Set to end of selected day (23:59:59:999)
        const endOfSelectedDay = new Date(selectedToDate);
        endOfSelectedDay.setHours(23, 59, 59, 999);
        
        // Apply rule: min(selected date at 23:59, current datetime)
        to.setTime(Math.min(endOfSelectedDay.getTime(), now.getTime()));
        break;
      default:
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    }

    return { from, to };
  }, [dateRange.value, customDateRange.from, customDateRange.to]);

  // Fetch assigned documents
  const fetchAssignedDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { from, to } = getDateRange();
      const summary = await insightsService.fetchDocumentsAssigned(from, to);
      setDocumentsAssigned(summary?.documentsAssigned || []);
      setMissingCount(summary?.missingCount || 0);
    } catch (err) {
      console.error('Error fetching assigned documents:', err);
      setError('Failed to fetch assignment data');
    } finally {
      setLoading(false);
    }
  }, [getDateRange, insightsService]);

  // Fetch assigned documents when modal opens or date range changes
  useEffect(() => {
    if (isOpen) {
      fetchAssignedDocuments();
    }
  }, [isOpen, dateRange, customDateRange, fetchAssignedDocuments]);


  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setDocumentsAssigned([]);
      setMissingCount(0);
      setError(null);
      setIsDropdownOpen(false);
    }
  }, [isOpen]);

  // Handle date range selection
  const handleDateRangeSelect = (option) => {
    // Update custom date range to reflect the new selection before switching
    if (option.value !== 'custom') {
      const now = new Date();
      let from;
      
      switch (option.value) {
        case 'today':
          from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        case 'yesterday':
          from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
          break;
        case 'last24h':
          from = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          break;
        case 'last7days':
          from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        default:
          from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      }
      
      setCustomDateRange({
        from: formatCalendarDate(from),
        to: todayCalendarDate(),
      });
    }
    
    setDateRange(option);
    setIsDropdownOpen(false);
  };

  // Handle custom date change
  const handleCustomDateChange = (field, value) => {
    setCustomDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const chartData = useMemo(() => {
    return Object.entries(assignmentCounts).map(([assigneeId, count]) => {
      const user = activeUsers.find(u => u.userId === assigneeId);
      if (user) {
        return { assignee: user.name, name: user.name, count, assigneeId, isTeam: false };
      }
      const team = teams.find(t => t.teamId === assigneeId);
      if (team) {
        return { assignee: team.teamName, name: team.teamName, count, assigneeId, isTeam: true };
      }
      return { assignee: 'Unknown', name: 'Unknown', count, assigneeId, isTeam: false };
    }).sort((a, b) => b.count - a.count);
  }, [assignmentCounts, activeUsers, teams]);

  const getSummaryStats = () => {
    const totalDocs = Object.values(assignmentCounts).reduce((sum, count) => sum + count, 0);
    const uniqueUsers = Object.keys(assignmentCounts).length;
    return { totalDocs, uniqueUsers };
  };

  const handleBarClick = useCallback((data) => {
    if (!onAssigneeClick) {
      return;
    }
    const assigneeId = getAssigneeIdFromChartClick(data);
    const entry = chartData.find((item) => item.assigneeId === assigneeId);
    const assigneeName = entry?.assignee || data?.assignee || data?.payload?.assignee;
    if (assigneeName) {
      onAssigneeClick(assigneeName);
      onClose();
    }
  }, [chartData, onAssigneeClick, onClose]);

  const dateRangeOptions = [
    { value: 'today', display: 'Today' },
    { value: 'yesterday', display: 'Yesterday' },
    { value: 'last24h', display: 'Last 24h' },
    { value: 'last7days', display: 'Last 7 days' },
    { value: 'custom', display: 'Custom' }
  ];

  if (!isOpen) return null;

  const { totalDocs, uniqueUsers } = getSummaryStats();

  return (
    <div className="document-assignee-modal-overlay">
      <div className="document-assignee-modal">
        <div className="document-assignee-modal-header">
          <h2>Document Assignee Tally</h2>
          <button className="document-assignee-modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className="document-assignee-modal-content">
          {error && (
            <div className="document-assignee-error">
              {error}
            </div>
          )}
          
          {missingCount > 0 && (
            <div className="document-assignee-warning">
              <FontAwesomeIcon icon={faTriangleExclamation} />
              {missingCount} document{missingCount > 1 ? 's were' : ' was'} excluded from the assignee tally because {missingCount > 1 ? 'they were' : 'it was'} deleted
            </div>
          )}
          
          <div className="document-assignee-date-selector">
            <label>Time Range:</label>
            {dateRange.value === 'custom' && (
              <div className="custom-date-inputs">
                <CustomDatePicker
                  value={customDateRange.from}
                  onChange={(value) => handleCustomDateChange('from', value)}
                  label="From"
                  max={customDateRange.to}
                />
                <CustomDatePicker
                  value={customDateRange.to}
                  onChange={(value) => handleCustomDateChange('to', value)}
                  label="To"
                  min={customDateRange.from}
                  max={todayCalendarDate()}
                />
              </div>
            )}
            <CustomDropdown
              options={dateRangeOptions}
              selectedOption={dateRange.display}
              onSelect={handleDateRangeSelect}
              isOpen={isDropdownOpen}
              toggleDropdown={() => setIsDropdownOpen(!isDropdownOpen)}
              keyExtractor={(option, index) => `date-range-${option.value || index}`}
            />
          </div>

          <div className="document-assignee-summary">
            {loading ? (
              'Loading assignment data...'
            ) : (
              `Total of ${totalDocs} documents assigned to ${uniqueUsers} user${uniqueUsers !== 1 ? 's' : ''} and teams in the selected time range.`
            )}
          </div>

          <div className="document-assignee-chart-container">
            <div className="document-assignee-chart-title">
              Documents Assigned by User
            </div>
            <p className="assignment-chart-click-hint">Click a bar to filter the documents list by that assignee.</p>
            
            {loading ? (
              <div className="document-assignee-loading">
                Loading chart data...
              </div>
            ) : chartData.length === 0 ? (
              <div className="document-assignee-no-data">
                No document assignments found for the selected time range.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={chartData}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 60
                  }}
                  style={{ cursor: 'pointer' }}
                  onClick={handleBarClick}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="assignee" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                  />
                  <YAxis 
                    allowDecimals={false}
                    domain={[0, 'dataMax']}
                  />
                  <Tooltip 
                    formatter={(value, name) => [value, 'Documents Assigned']}
                    labelFormatter={(label) => `User: ${label}`}
                    cursor={{ fill: 'rgba(0, 0, 0, 0.06)', cursor: 'pointer' }}
                  />
                  <Bar 
                    dataKey="count" 
                    fill="#3498db"
                    name="Documents Assigned"
                    onClick={handleBarClick}
                    cursor="pointer"
                    background={{ fill: 'transparent', cursor: 'pointer' }}
                  >
                    {chartData.map((entry) => (
                      <Cell
                        key={entry.assigneeId}
                        fill={entry.isTeam ? "#9b59b6" : "#3498db"}
                        cursor="pointer"
                        onClick={() => handleBarClick(entry)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentAssigneeModal;
